import React, { useState } from 'react';
import Isvg from 'react-inlinesvg';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

import GenericButton from 'components/Button';
import { Card } from 'components/Card';
import { Button, ButtonGroup, Form, Input, PhoneNumberInput } from 'components/HookForm';
import Message from 'components/Message';
import When from 'components/When';
import { ICONS, ILLUSTRATIONS } from 'constants/icons';
import { EMAIL_REGEX } from 'constants/logic';
import URLS from 'constants/urls';
import appMessages from 'features/App/App.messages';
import braze, { BRAZE_CUSTOM_EVENTS } from 'helpers/braze';
import { bem, classNameWithModifiers as withMods } from 'helpers/class-name';
import { transformKeysToSnakeCase } from 'helpers/core';
import { reportError } from 'helpers/error-logging';
import { httpClient } from 'helpers/http';
import Segment from 'helpers/segment';

import { integrationsFormMessages as messages } from './Integrations.messages';

import './IntegrationRequestModal.scss';

const FORM_NAME = 'integration-request-form';
const { block, element } = bem('integration-request-modal');

const IntegrationRequestModal = ({
  createErrorBanner,
  handleCloseModal,
  initialValues,
  description,
  guideLink,
  integratorName,
  logoImageUrl,
  sfdcApiValue,
  venueId,
}) => {
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);

  const intl = useIntl();
  const closeAriaLabel = intl.formatMessage(appMessages.close);

  const { email, firstName, lastName } = initialValues;
  const defaultValues = {
    fullName: firstName && lastName ? `${firstName} ${lastName}` : '',
    email,
  };

  const handleSubmit = async (formValues, formAsyncFns) => {
    const { resolveSubmit, rejectSubmit } = formAsyncFns;
    const { email: newEmail, fullName, phoneNumber } = formValues;

    const nameParts = fullName.split(' ');
    const first = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : nameParts[0];
    const last = nameParts.length > 1 ? nameParts[nameParts.length - 1] : '';

    const payload = transformKeysToSnakeCase({
      integrator: sfdcApiValue,
      firstName: first,
      lastName: last,
      email: newEmail,
      phone: phoneNumber || null,
    });

    try {
      await httpClient.post(URLS.integrators.requestToIntegrate(venueId), payload);
      resolveSubmit();
      setIsFormSubmitted(true);
      braze.trackEvent(BRAZE_CUSTOM_EVENTS.INTEGRATION_FORM_SUBMITTED);
    } catch (error) {
      rejectSubmit();
      handleCloseModal();
      reportError(error);
      return createErrorBanner(messages.genericError);
    }
  };

  return (
    <Card flush className={block()}>
      <div className={element('close-btn-wrapper')}>
        <button aria-label={closeAriaLabel} className={element('close-btn')} type="button" onClick={handleCloseModal}>
          <Isvg src={ICONS.crossRegularCircle} />
        </button>
      </div>
      <div className={element('content-container')}>
        <div className={element('title-container')}>
          <When predicate={!logoImageUrl}>
            <span className={element('logo-icon')}>{integratorName[0]}</span>
          </When>
          <When predicate={!!logoImageUrl}>
            <img className={element('logo-image')} src={logoImageUrl} alt={`${integratorName} logo`} />
          </When>
          <Message message={messages.title} values={{ integratorName }} />
        </div>
        <When predicate={!isFormSubmitted}>
          <When predicate={!!description}>
            <div className={element('description-wrapper')}>
              <Message message={description} />
            </div>
          </When>
          <Form id={FORM_NAME} defaultValues={defaultValues} onSubmit={handleSubmit}>
            {({ formState }) => {
              const { isDirty, isSubmitting } = formState;
              return (
                <>
                  <Input
                    name="fullName"
                    disabled={isSubmitting}
                    label={messages.nameLabel}
                    placeholder={messages.namePlaceholder}
                    type="text"
                    required
                  />
                  <Input
                    name="email"
                    disabled={isSubmitting}
                    label={messages.emailLabel}
                    placeholder={messages.emailPlaceholder}
                    pattern={{
                      value: EMAIL_REGEX,
                      message: messages.invalidEmail,
                    }}
                    type="email"
                    required
                  />
                  <PhoneNumberInput
                    name="phoneNumber"
                    disabled={isSubmitting}
                    label={messages.phoneLabel}
                    placeholder={messages.phonePlaceholder}
                    addInternationalOption={false}
                    countryCode="US"
                    optional
                  />
                  <ButtonGroup className={element('button-group')} divider={false} flush>
                    <Message
                      as={Button}
                      className={withMods(element('cancel-btn'), 'bt--sm', 'bt--width-full', {
                        loading: isSubmitting,
                      })}
                      disabled={isSubmitting}
                      message={appMessages.cancel}
                      onClick={handleCloseModal}
                      styled="primary-outline"
                      type={Button.TYPES.BUTTON}
                    />
                    <Message
                      as={Button}
                      className={withMods(element('submit-btn'), 'bt--sm', 'bt--width-full', 'ml-', {
                        loading: isSubmitting,
                      })}
                      disabled={(!defaultValues.fullName || !defaultValues.email) && !isDirty}
                      message={appMessages.submit}
                      onClick={() => Segment.event('Integrator Form Submission', { integratorName, venueId })}
                      styled="primary"
                      type={Button.TYPES.SUBMIT}
                      waiting={isSubmitting}
                      waitSpinner
                    />
                  </ButtonGroup>
                </>
              );
            }}
          </Form>
        </When>
        <When predicate={isFormSubmitted}>
          <div className={element('description-wrapper')}>
            <Message message={messages.submitted} />
          </div>
          <When predicate={!!guideLink}>
            <a
              className={element('integration-guide-link')}
              href={guideLink}
              onClick={() => Segment.event('Click Integrator Guide', { integratorName, venueId })}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${integratorName} integration guide`}>
              <div className={element('integration-guide-container')}>
                <div className={element('guide-text-wrapper')}>
                  <Message
                    className={element('guide-title')}
                    message={messages.integrationGuide}
                    values={{ integratorName }}
                  />
                  <When predicate={!!description}>
                    <Message className={element('guide-description')} message={description} />
                  </When>
                </div>
                <div className={element('guide-illustration-wrapper')}>
                  <Isvg src={ILLUSTRATIONS.laptop} />
                </div>
              </div>
            </a>
          </When>
          <div className={element('dismiss-wrapper')}>
            <Message
              as={GenericButton}
              className={element('dismiss-btn', 'bt--sm', 'bt--width-full')}
              message={messages.dismiss}
              onClick={handleCloseModal}
              styled="primary"
            />
          </div>
        </When>
      </div>
    </Card>
  );
};

IntegrationRequestModal.propTypes = {
  createErrorBanner: PropTypes.func,
  handleCloseModal: PropTypes.func,
  initialValues: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
  description: PropTypes.string,
  guideLink: PropTypes.string,
  integratorName: PropTypes.string,
  logoImageUrl: PropTypes.string,
  sfdcApiValue: PropTypes.string,
  venueId: PropTypes.number,
};

export default IntegrationRequestModal;
